import { ComponentPropsWithRef, forwardRef, useMemo } from 'react';
import { Journals } from '../../../../types/Journals.types';
import {
  ContextProp,
  TableBodyProps,
  TableComponents,
  TableProps,
  TableVirtuoso,
} from 'react-virtuoso';
import { useTranslation } from 'react-i18next';
import { MasterlistJournalCheck } from '../../../../types/masterlistCheck.types';
import { ErrorBadge } from './ErrorBadge';
import { MasterlistStatus } from './MasterlistStatus';
import { checkFor } from './masterlistCheck';
import {
  isMissingColumn,
  isMissingData,
  journalCellMissing,
  journalDescribedBy,
  journalRowId,
  masterlistStatusId,
  missingDataId,
} from './utils';
import {
  Table,
  TableBody,
  TableCell,
  Link,
  TableHead,
  TableRow,
} from '@mui/material';

interface JournalsTableDesktopProps {
  journals: Journals[];
  onJournalClick: (journal: Journals) => void;
  preview: boolean;
  /** Set when the masterlist column is shown; journals without a check get an empty cell. */
  checksByJournalId?: Map<string, MasterlistJournalCheck>;
}

interface JournalsTableContext {
  tableLabel: string;
  onJournalClick: (journal: Journals) => void;
  checksByJournalId?: Map<string, MasterlistJournalCheck>;
}

// Defined at module level: components created during render get a new type on every render,
// which remounts every row and drops keyboard focus.
const InnerTable = ({
  context,
  ...props
}: TableProps & ContextProp<JournalsTableContext>) => (
  <Table {...props} sx={{ minWidth: 650 }} aria-label={context.tableLabel} />
);

const InnerTableBody = forwardRef<
  HTMLTableSectionElement,
  TableBodyProps & ContextProp<JournalsTableContext>
>(({ context: _context, ...props }, ref) => <TableBody {...props} ref={ref} />);
InnerTableBody.displayName = 'TableBody';

const InnerTableHead = forwardRef<
  HTMLTableSectionElement,
  ComponentPropsWithRef<typeof TableHead> & ContextProp<JournalsTableContext>
>(({ context: _context, ...props }, ref) => <TableHead {...props} ref={ref} />);
InnerTableHead.displayName = 'TableHead';

const InnerTableRow = forwardRef<
  HTMLTableRowElement,
  ComponentPropsWithRef<typeof TableRow> & {
    item: Journals;
  } & ContextProp<JournalsTableContext>
>(({ item: journal, style, context, ...rest }, ref) => {
  const { t } = useTranslation();
  const { onJournalClick, checksByJournalId } = context;
  const rowLabel = journal.name
    ? t('agreements.editJournalRow', { name: journal.name })
    : t('agreements.editJournalRowUnnamed');
  const rowId = journalRowId(
    'desktop',
    journal,
    (rest as { 'data-index'?: number })['data-index'] ?? 0
  );
  const describedBy = journalDescribedBy(
    rowId,
    journal,
    !!checkFor(checksByJournalId, journal)
  );
  return (
    <TableRow
      tabIndex={0}
      aria-label={rowLabel}
      aria-describedby={describedBy}
      onKeyDown={(e) => {
        // Keys on the journal's link are the link's: Enter there follows it.
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onJournalClick(journal);
        }
      }}
      {...rest}
      ref={ref}
      key={`${journal.issn_e}`}
      style={style}
      sx={{
        cursor: 'pointer',
        '&:focus-visible': {
          outline: '2px solid var(--color-scifree-teal)',
          outlineOffset: '-2px',
        },
        ...(isMissingData(journal)
          ? {
              backgroundColor: 'var(--color-very-pale-orange)',
              '&:hover': { backgroundColor: 'var(--color-very-pale-orange)' },
            }
          : {}),
      }}
      onClick={() => onJournalClick(journal)}
    />
  );
});
InnerTableRow.displayName = 'TableRow';

const tableComponents: TableComponents<Journals, JournalsTableContext> = {
  Table: InnerTable,
  TableBody: InnerTableBody,
  TableHead: InnerTableHead,
  TableRow: InnerTableRow,
};

export function JournalsTableDesktop({
  journals,
  onJournalClick,
  preview,
  checksByJournalId,
}: JournalsTableDesktopProps) {
  const { t } = useTranslation();
  const showMasterlist = !!checksByJournalId;
  const tableLabel = preview
    ? (t('agreements.journalTable.previewLabel') as string)
    : (t('agreements.journalTable.label') as string);
  const context = useMemo<JournalsTableContext>(
    () => ({
      tableLabel,
      onJournalClick,
      checksByJournalId,
    }),
    [tableLabel, onJournalClick, checksByJournalId]
  );

  return (
    <TableVirtuoso
      style={{ height: '640px' }}
      context={context}
      components={tableComponents}
      fixedHeaderContent={() => (
        <TableRow>
          <TableCell sx={{ width: showMasterlist ? '20%' : '25%' }}>
            {t('agreements.journal')} <span aria-hidden="true">*</span>
            <span className="sr-only">
              {t('agreements.journalTable.required')}
            </span>
          </TableCell>
          <TableCell sx={{ width: '10%' }}>{t('agreements.imprint')}</TableCell>
          <TableCell sx={{ width: '10%' }}>
            {t('agreements.journalTable.payment')}
          </TableCell>
          <TableCell sx={{ width: '10%' }}>
            {t('agreements.journalTable.licenses')}
          </TableCell>
          <TableCell sx={{ width: '10%' }}>
            <>
              {t('agreements.journalTable.publishingModel')}{' '}
              <span aria-hidden="true">*</span>
              <span className="sr-only">
                {t('agreements.journalTable.required')}
              </span>
            </>
          </TableCell>
          <TableCell sx={{ width: '7%' }}>{t('agreements.issne')}</TableCell>
          <TableCell sx={{ width: '7%' }}>{t('agreements.issnp')}</TableCell>
          <TableCell sx={{ width: showMasterlist ? '10%' : '21%' }}>
            {t('agreements.journalTable.subjects')}
          </TableCell>
          {showMasterlist && (
            <TableCell sx={{ width: '16%' }}>
              {t('agreements.masterlistCheck.columnHeader')}
            </TableCell>
          )}
        </TableRow>
      )}
      data={journals}
      itemContent={(index, journal) => {
        const rowId = journalRowId('desktop', journal, index);
        const check = checkFor(checksByJournalId, journal);
        const url = journal.url?.trim();
        const cellMissing = journalCellMissing(journal);
        const isNameMissing = cellMissing === 'name';
        const missingInfo = cellMissing
          ? (t(`agreements.journalTable.missing.${cellMissing}`) as string)
          : '';

        const journalNameElement = isNameMissing ? null : journal.name;
        const journalUrl = url ? (
          <Link
            href={url}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            {journalNameElement}
          </Link>
        ) : (
          <span>{journalNameElement}</span>
        );

        return (
          <>
            <TableCell>
              {missingInfo ? (
                <ErrorBadge
                  title={missingInfo}
                  id={missingDataId(rowId, 'name')}
                >
                  {journalNameElement}
                </ErrorBadge>
              ) : (
                journalUrl
              )}
            </TableCell>
            <TableCell>{journal.imprint}</TableCell>
            <TableCell>
              {isMissingColumn(journal, 'payment') ? (
                <ErrorBadge
                  title={t('agreements.journalTable.missing.payment') as string}
                  id={missingDataId(rowId, 'payment')}
                />
              ) : (
                journal.payment
              )}
            </TableCell>
            <TableCell>
              {isMissingColumn(journal, 'licenses') ? (
                <ErrorBadge
                  title={
                    t('agreements.journalTable.missing.licenses') as string
                  }
                  id={missingDataId(rowId, 'licenses')}
                />
              ) : journal.licenses ? (
                journal.licenses.join(', ')
              ) : (
                ''
              )}
            </TableCell>
            <TableCell>
              {isMissingColumn(journal, 'publishing_model') ? (
                <ErrorBadge
                  title={
                    t(
                      'agreements.journalTable.missing.publishingModel'
                    ) as string
                  }
                  id={missingDataId(rowId, 'publishing_model')}
                />
              ) : (
                journal.publishing_model
              )}
            </TableCell>
            <TableCell>{journal.issn_e}</TableCell>
            <TableCell>{journal.issn_p}</TableCell>
            <TableCell>{journal.subjects}</TableCell>
            {showMasterlist && (
              <TableCell>
                {check && (
                  <MasterlistStatus
                    id={masterlistStatusId(rowId)}
                    check={check}
                  />
                )}
              </TableCell>
            )}
          </>
        );
      }}
    />
  );
}
